import { Header } from "@/components/Header";
import { Helmet } from "react-helmet-async";

const Privacy = () => {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Privacy Policy - MacroVault</title>
        <meta name="description" content="Learn how MacroVault collects, uses, and protects your information." />
        <link rel="canonical" href="https://findmacros.com/privacy" />
      </Helmet>
      <Header />
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <h1 className="text-4xl font-bold mb-6">Privacy Notice</h1>
        <div className="prose prose-slate dark:prose-invert max-w-none">
          <p className="text-muted-foreground mb-4">Last updated: November 2025</p>

          <p className="mb-6">
            MacroVault is a cloud database for storing and sharing VBA macros. This Privacy Notice explains how
            MacroFlow LLC (“we,” “us,” “our”) collects, uses, and protects your information when you use MacroVault (the
            “Service”).
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Information We Collect</h2>
          <ul>
            <li>
              <strong>Account Information:</strong> email, name, company/organization, password (hashed), and account
              verification status.
            </li>
            <li>
              <strong>User Content:</strong> uploaded macros, metadata (titles, descriptions, tags), visibility settings
              (public/private), comments, and stars/likes.
            </li>
            <li>
              <strong>Usage & Device Data:</strong> pages viewed, features used, timestamps, referrers, approximate
              location derived from IP, and device/browser details.
            </li>
            <li>
              <strong>Communications:</strong> messages you send us (support, feedback) and your email preferences.
            </li>
            <li>
              <strong>Payments (future):</strong> if/when we enable paid features, limited billing details will be
              processed by our payment processor; we will not store full payment card numbers on our servers.
            </li>
            <li>
              <strong>Cookies & Similar Technologies:</strong> functional cookies to keep you signed in and remember
              preferences, and analytics cookies to understand product usage.
            </li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8 mb-4">How We Use Your Information</h2>
          <ul>
            <li>Provide, secure, and maintain the Service (account sign-in, verification, uptime).</li>
            <li>Store and display your macros per your settings (public or private).</li>
            <li>Improve features and performance, including analytics, testing, and troubleshooting.</li>
            <li>Communicate with you about updates, security alerts, and support.</li>
            <li>Enforce our policies and protect MacroVault, our users, and the public from abuse or fraud.</li>
            <li>
              Prepare for future paid features and marketplace functionality (e.g., billing and payouts via a payment
              processor).
            </li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Information Sharing</h2>
          <p>We do not sell your personal information. We share information only as needed to run the Service:</p>
          <ul>
            <li>
              <strong>Service Providers:</strong> We host data on Supabase (database, auth, and functions) and may use
              analytics and email providers. These vendors process data on our behalf under confidentiality and security
              obligations.
            </li>
            <li>
              <strong>Public Content:</strong> If you choose to make a macro public, its content and associated metadata
              may be viewable and downloadable by others.
            </li>
            <li>
              <strong>Legal & Safety:</strong> We may disclose information if required by law or to protect rights,
              safety, and security of users, the public, or MacroFlow LLC.
            </li>
            <li>
              <strong>Business Transfers:</strong> If we engage in a merger, acquisition, or asset sale, your
              information may be transferred subject to this Notice.
            </li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Cookies & Analytics</h2>
          <p>
            We use functional cookies to keep you signed in and remember settings, and analytics cookies to understand
            how the Service is used. You can control cookies in your browser settings; disabling cookies may limit
            functionality.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Data Security</h2>
          <p>
            We employ technical and organizational measures appropriate to the nature of the data, including encryption
            in transit, access controls, and activity logging. No method of transmission or storage is 100% secure; we
            work to continuously improve our safeguards.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Data Location & Transfers</h2>
          <p>
            We currently host MacroVault on Supabase infrastructure, which stores data in regions we select (presently
            the United States). If we transfer data across regions, we will use appropriate protections consistent with
            this Notice.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Your Choices & Rights</h2>
          <ul>
            <li>
              <strong>Access/Update:</strong> View and update profile details in your account settings.
            </li>
            <li>
              <strong>Download/Portability:</strong> You may request an export of your personal data and/or your own
              private content.
            </li>
            <li>
              <strong>Deletion:</strong> You can delete your account or request deletion of your personal information,
              subject to legal or legitimate business needs (e.g., security, abuse prevention, audit logs).
            </li>
            <li>
              <strong>Email Preferences:</strong> You can opt out of non-essential emails (e.g., newsletters). We may
              still send transactional emails (verification, security, service updates).
            </li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Account Verification & Visibility</h2>
          <p>
            MacroVault requires user accounts and includes a verification process. You control whether your macros are
            public or private. Public content is discoverable by others and may be indexed by search. Private content is
            only accessible to you and anyone you explicitly share it with (if such sharing is enabled).
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Children’s Privacy</h2>
          <p>
            The Service is not intended for children under 13. If you believe a child under 13 has provided personal
            information, contact us and we will take appropriate steps to remove it.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Retention</h2>
          <p>
            We retain personal information for as long as necessary to provide the Service and for legitimate business
            purposes (e.g., security, fraud prevention, and record-keeping). When no longer needed, we take steps to
            delete or anonymize information.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Third-Party Links</h2>
          <p>
            The Service may link to third-party sites or services. Their privacy practices are governed by their own
            policies. Please review them independently.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Governing Law</h2>
          <p>
            This Notice is governed by the laws of the State of New York, USA. Additional rights may apply depending on
            your location.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Changes to This Notice</h2>
          <p>
            We may update this Privacy Notice to reflect changes to our practices. If we make material changes, we will
            notify you by email or through the Service. The “Last updated” date reflects the most recent revision.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Contact Us</h2>
          <p>
            For questions or requests about this Privacy Notice or your data, contact{" "}
            <a href="mailto:privacy@macroflow.ai">privacy@macroflow.ai</a>. You can also reach us at:
          </p>
          <p className="mb-0">
            <strong>MacroFlow LLC</strong>
            <br />
            Attn: Privacy
            <br />
            New York, USA
          </p>
        </div>
      </main>
    </div>
  );
};

export default Privacy;
